import type { PreviewChartDataProps } from '@services/api/preview/types'

export type ParsedChartBar = {
  value: number
  color: string
}

export type ParsedChartAccount = {
  name: string
  /** RN03: GM é o único percentual — todas as demais contas são monetárias (RN04) */
  formatAsPercent?: boolean
  /** true quando o backend envia desvio por semana (weekNDeviationAmount) em vez de um desvio único */
  weeklyDeviation?: boolean
  deviationAmount?: number
  week1DeviationAmount?: number
  week2DeviationAmount?: number
  week3DeviationAmount?: number
  week4DeviationAmount?: number
  week5DeviationAmount?: number
  budget: ParsedChartBar
  week1: ParsedChartBar
  week2: ParsedChartBar
  week3: ParsedChartBar
  week4: ParsedChartBar
  week5: ParsedChartBar
}

/** RN01: ordem fixa — Faturamento → Consumo → Pessoal → Depreciação → Outras Despesas → Bonificação → GP → GM */
export const parseDataChart = (data: PreviewChartDataProps | null): ParsedChartAccount[] => {
  if (!data) return []

  const chartData: ParsedChartAccount[] = []

  if (data.billing) {
    chartData.push({
      name: 'Faturamento',
      deviationAmount: data.billing.deviationAmount ?? 0,
      budget: { value: data.billing.budget || 0, color: '#7B8794' },
      week1: { value: data.billing.week1 || 0, color: '#C3E3E1' },
      week2: { value: data.billing.week2 || 0, color: '#A1CFCE' },
      week3: { value: data.billing.week3 || 0, color: '#7EBBBC' },
      week4: { value: data.billing.week4 || 0, color: '#5BA7AA' },
      week5: { value: data.billing.week5 || 0, color: '#3A9397' }
    })
  }

  if (data.consumption) {
    chartData.push({
      name: 'Consumo',
      deviationAmount: data.consumption.deviationAmount ?? 0,
      budget: { value: data.consumption.budget ?? 0, color: '#7B8794' },
      week1: { value: data.consumption.week1 ?? 0, color: '#FDE8D1' },
      week2: { value: data.consumption.week2 ?? 0, color: '#FACFAD' },
      week3: { value: data.consumption.week3 ?? 0, color: '#F8A77D' },
      week4: { value: data.consumption.week4 ?? 0, color: '#F58F5C' },
      week5: { value: data.consumption.week5 ?? 0, color: '#F2793A' }
    })
  }

  if (data.personal) {
    chartData.push({
      name: 'Pessoal',
      deviationAmount: data.personal.deviationAmount ?? 0,
      budget: { value: data.personal.budget || 0, color: '#7B8794' },
      week1: { value: data.personal.week1 || 0, color: '#FDE2E2' },
      week2: { value: data.personal.week2 || 0, color: '#FAC4C4' },
      week3: { value: data.personal.week3 || 0, color: '#F79C9C' },
      week4: { value: data.personal.week4 || 0, color: '#E98585' },
      week5: { value: data.personal.week5 || 0, color: '#D96060' }
    })
  }

  if (data.depreciation) {
    chartData.push({
      name: 'Depreciação',
      deviationAmount: data.depreciation.deviationAmount ?? 0,
      budget: { value: data.depreciation.budget || 0, color: '#7B8794' },
      week1: { value: data.depreciation.week1 || 0, color: '#F5E2DA' },
      week2: { value: data.depreciation.week2 || 0, color: '#E7D0BE' },
      week3: { value: data.depreciation.week3 || 0, color: '#DABEA2' },
      week4: { value: data.depreciation.week4 || 0, color: '#CCAD86' },
      week5: { value: data.depreciation.week5 || 0, color: '#BF9C6A' }
    })
  }

  if (data.otherExpenses) {
    chartData.push({
      name: 'Outras Despesas',
      deviationAmount: data.otherExpenses.deviationAmount ?? 0,
      budget: { value: data.otherExpenses.budget || 0, color: '#7B8794' },
      week1: { value: data.otherExpenses.week1 || 0, color: '#F7E0E0' },
      week2: { value: data.otherExpenses.week2 || 0, color: '#E8B8C0' },
      week3: { value: data.otherExpenses.week3 || 0, color: '#D48A9A' },
      week4: { value: data.otherExpenses.week4 || 0, color: '#BB5C75' },
      week5: { value: data.otherExpenses.week5 || 0, color: '#9C3F58' }
    })
  }

  if (data.bonification) {
    chartData.push({
      name: 'Bonificação',
      deviationAmount: data.bonification.deviationAmount ?? 0,
      budget: { value: data.bonification.budget || 0, color: '#7B8794' },
      week1: { value: data.bonification.week1 || 0, color: '#C2F0D7' },
      week2: { value: data.bonification.week2 || 0, color: '#A6E2C1' },
      week3: { value: data.bonification.week3 || 0, color: '#89D3AA' },
      week4: { value: data.bonification.week4 || 0, color: '#6DC593' },
      week5: { value: data.bonification.week5 || 0, color: '#51B77D' }
    })
  }

  /** RN04: GP é sempre monetário, mas o desvio vem por semana (como o GM) */
  if (data.gross?.grossProfit) {
    chartData.push({
      name: 'GP - Gross Profit',
      weeklyDeviation: true,
      week1DeviationAmount: data.gross.grossProfit.week1DeviationAmount ?? 0,
      week2DeviationAmount: data.gross.grossProfit.week2DeviationAmount ?? 0,
      week3DeviationAmount: data.gross.grossProfit.week3DeviationAmount ?? 0,
      week4DeviationAmount: data.gross.grossProfit.week4DeviationAmount ?? 0,
      week5DeviationAmount: data.gross.grossProfit.week5DeviationAmount ?? 0,
      budget: { value: data.gross.grossProfit.budget || 0, color: '#7B8794' },
      week1: { value: data.gross.grossProfit.week1 || 0, color: '#D9D2F0' },
      week2: { value: data.gross.grossProfit.week2 || 0, color: '#C3B8E8' },
      week3: { value: data.gross.grossProfit.week3 || 0, color: '#AD9EE0' },
      week4: { value: data.gross.grossProfit.week4 || 0, color: '#8F79D6' },
      week5: { value: data.gross.grossProfit.week5 || 0, color: '#6E55C6' }
    })
  }

  /** RN03: GM sempre percentual */
  if (data.gross?.grossMargin) {
    chartData.push({
      name: 'GM - Gross Margin',
      formatAsPercent: true,
      weeklyDeviation: true,
      week1DeviationAmount: data.gross.grossMargin.week1DeviationAmount ?? 0,
      week2DeviationAmount: data.gross.grossMargin.week2DeviationAmount ?? 0,
      week3DeviationAmount: data.gross.grossMargin.week3DeviationAmount ?? 0,
      week4DeviationAmount: data.gross.grossMargin.week4DeviationAmount ?? 0,
      week5DeviationAmount: data.gross.grossMargin.week5DeviationAmount ?? 0,
      budget: { value: data.gross.grossMargin.budget || 0, color: '#7B8794' },
      week1: { value: data.gross.grossMargin.week1 || 0, color: '#BDDEF3' },
      week2: { value: data.gross.grossMargin.week2 || 0, color: '#9CD1EE' },
      week3: { value: data.gross.grossMargin.week3 || 0, color: '#7CC4E9' },
      week4: { value: data.gross.grossMargin.week4 || 0, color: '#5BB7E4' },
      week5: { value: data.gross.grossMargin.week5 || 0, color: '#3AA9DF' }
    })
  }

  return chartData
}
